import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { useEffect, useState } from "react";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { ToastMessage } from "../../Utils/UIMessages";
import "./PushPanel.scss";

// Notifiche degli allarmi su questo dispositivo (Web Push, titolare 01/11/2026): in Impostazioni sotto «Installa App».
// Ogni telefono o PC si iscrive per conto suo; le notifiche si aggiungono alle email e alla finestra dell'allarme.
type PushState = "loading" | "insecure" | "ios-install" | "unsupported" | "denied" | "off" | "on";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// chiave pubblica VAPID (base64url) nel formato che vuole il browser
const keyBytes = (base64: string) => {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const currentSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
};

const PushPanel = () => {
  const [state, setState] = useState<PushState>("loading");
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    if (!window.isSecureContext) return setState("insecure");
    if (!supported()) return setState(isIOS() && !isStandalone() ? "ios-install" : "unsupported");
    if (Notification.permission === "denied") return setState("denied");
    if (Notification.permission !== "granted") return setState("off");
    currentSubscription()
      .then((sub) => {
        if (!sub) return setState("off");
        // il server potrebbe non averla piu' (iscrizione tolta o database nuovo): la si riallinea in silenzio
        ApiService.PushController.Subscribe(sub.toJSON());
        setState("on");
      })
      .catch(() => setState("off"));
  }, []);

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // prima cosa dopo il tocco: l'iPhone chiede il permesso solo se la richiesta parte subito dal pulsante
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "off");
        return;
      }
      const key: IAPIResponse = await ApiService.PushController.GetKey();
      if (key.error !== null || !key.parsed?.publicKey) throw new Error("chiave non disponibile");
      const reg = await navigator.serviceWorker.ready;
      const old = await reg.pushManager.getSubscription();
      const sub =
        old ??
        (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key.parsed.publicKey) }));
      const saved: IAPIResponse = await ApiService.PushController.Subscribe(sub.toJSON());
      if (saved.error !== null) throw new Error(saved.error);
      setState("on");
      ToastMessage("success", "Notifiche attive su questo dispositivo.");
    } catch (e: any) {
      ToastMessage("error", "Impossibile attivare le notifiche: " + (e?.message || "errore"));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (!sub) return setState("off");
      const r: IAPIResponse = await ApiService.PushController.SendTest(sub.endpoint);
      if (r.error === null && r.parsed?.ok) ToastMessage("success", "Prova inviata: la notifica arriva tra pochi secondi.");
      else ToastMessage("error", "Prova non riuscita: " + (r.parsed?.error || r.error || "errore"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await ApiService.PushController.Unsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      setState("off");
      ToastMessage("success", "Notifiche disattivate su questo dispositivo.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") return null;
  return (
    <div>
      <div className="app-generic-title">Notifiche degli allarmi</div>
      {state === "insecure" && (
        <div className="push-help">Le notifiche funzionano solo aprendo il sito dall'indirizzo che comincia con https.</div>
      )}
      {state === "ios-install" && (
        <div className="push-help">
          Sull'iPhone le notifiche arrivano solo con l'app installata: tocca prima <b>«Installa App»</b> qui sopra, poi
          apri l'app dall'icona sulla schermata Home e torna qui.
        </div>
      )}
      {state === "unsupported" && (
        <div className="push-help">
          Questo browser non riceve notifiche. Sul telefono usa Chrome (Android) o l'app installata (iPhone, iOS 16.4 o
          più recente).
        </div>
      )}
      {state === "denied" && (
        <div className="push-help">
          Le notifiche sono bloccate su questo dispositivo. Per sbloccarle:
          {isIOS() ? (
            <ol>
              <li>apri le <b>Impostazioni</b> dell'iPhone, poi <b>Notifiche</b>;</li>
              <li>tocca <b>SSL Cloud</b> e accendi <b>«Consenti notifiche»</b>;</li>
              <li>torna qui e ricarica la pagina.</li>
            </ol>
          ) : (
            <ol>
              <li>tocca il lucchetto (o la «i») accanto all'indirizzo, in alto;</li>
              <li>alla voce <b>Notifiche</b> scegli <b>Consenti</b>;</li>
              <li>ricarica la pagina.</li>
            </ol>
          )}
        </div>
      )}
      {state === "off" && (
        <>
          <div className="push-help">
            Ricevi un avviso su questo dispositivo quando scatta un allarme, anche con l'app chiusa.
          </div>
          <PrimaryButton
            className="push-main"
            iconProps={{ iconName: "Ringer" }}
            text={busy ? "Attivazione..." : "Attiva le notifiche"}
            disabled={busy}
            onClick={activate}
          />
        </>
      )}
      {state === "on" && (
        <>
          <div className="push-on">Notifiche attive su questo dispositivo.</div>
          <div className="push-actions">
            <PrimaryButton iconProps={{ iconName: "Send" }} text="Invia una prova" disabled={busy} onClick={sendTest} />
            <DefaultButton text="Disattiva" disabled={busy} onClick={deactivate} />
          </div>
        </>
      )}
    </div>
  );
};

export default PushPanel;
